import {
  ChevronsUpDown,
  LogOut,
  PaletteIcon,
  Plus,
  User2Icon,
  UserCog2,
  Users,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useNavigate } from "react-router-dom";
import {
  SignOutButton,
  useClerk,
  useSession,
  useSessionList,
} from "@clerk/react";
import AppearanceSettings from "./appearance-settings";

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
}) {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { session: currentSession } = useSession();
  const { sessions, setActive } = useSessionList();
  const { openSignIn } = useClerk();

  const otherSessions = (sessions ?? []).filter(
    (s) => s.id !== currentSession?.id && s.status === "active",
  );

  const handleSwitch = async (sessionId: string) => {
    await setActive?.({ session: sessionId });
    // Optional: reload so all data refetches under the new user
    window.location.reload();
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <Avatar variant="square">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback>
                    <User2Icon />
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto opacity-60" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-62"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-3 p-1.5 text-left text-sm">
                <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <Avatar variant="square" className="size-9">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback>CN</AvatarFallback>
                  </Avatar>
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium text-foreground">
                    {user.name}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => navigate("/dashboard/settings/personal")}
              >
                <UserCog2 />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <PaletteIcon className="mr-2 size-4 opacity-60" />
                  Edit Appearence
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="min-w-56 rounded-lg">
                    <AppearanceSettings />
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Users className="mr-2 size-4 opacity-60" />
                  Switch account
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="min-w-56 rounded-lg">
                    {otherSessions.map((s) => (
                      <DropdownMenuItem
                        key={s.id}
                        onClick={() => handleSwitch(s.id)}
                      >
                        <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                          <Avatar variant="square">
                            <AvatarImage
                              src={s.publicUserData?.imageUrl}
                              alt={s.publicUserData?.identifier ?? "account"}
                            />
                            <AvatarFallback>
                              <User2Icon className="size-4" />
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div className="grid flex-1 text-left leading-tight">
                          <span className="truncate text-sm">
                            {s.publicUserData?.firstName
                              ? `${s.publicUserData.firstName} ${s.publicUserData.lastName ?? ""}`.trim()
                              : s.publicUserData?.identifier}
                          </span>
                          {s.publicUserData?.firstName && (
                            <span className="truncate text-xs text-muted-foreground">
                              {s.publicUserData?.identifier}
                            </span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {otherSessions.length > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={() => openSignIn()}>
                      <Plus className="size-4" />
                      Add account
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <SignOutButton signOutOptions={{ sessionId: currentSession?.id }}>
                <DropdownMenuItem variant="destructive">
                  <LogOut />
                  Sign out
                </DropdownMenuItem>
              </SignOutButton>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
